import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect, createRef } from 'react';
import { COLORS } from '../../EatClubTheme';
import AccordionChevron from '../../assets/accordion_chevron.svg';
import IconPlus from '../../assets/icon_plus.svg';
import IconMinus from '../../assets/icon_minus.svg';
import { Box } from '../Box';
import { Spacer } from '../Spacer';

/**
 * A list of collapsable / items
 */
export const Accordion = ({ className, style, multiple, items, variant, defaultOpen }) => {
  const [hover, setHover] = useState(null);
  const [focus, setFocus] = useState(null);
  const [expandedItems, setExpandedItems] = useState([]);

  // Create a ref for every item, so we can tell how high the box should be
  const itemRefs = useRef(
    items?.reduce((accum, object) => {
      const updated = { ...accum };
      updated[object.id] = createRef();
      return updated;
    }, {}),
  );

  const styles = {
    container: {
      ...style,
      ...style?.container,
    },
    item: {
      // height: "64px",
      borderBottom: '1px solid #CDCDCD',
      ...style?.item,
    },
    itemActive: {
      // backgroundColor: "#ccc",
      // fontWeight: 'bold',
      ...style?.itemActive,
    },
    itemHover: {
      // backgroundColor: '#ccc',
      fontWeight: 'medium',
      filter: 'brightness(98%)',
      ...style?.itemHover,
    },
    itemFocus: {
      // filter: 'brightness(98%)',
      outline: '1px solid black',
      ...style?.itemFocus,
    },
    summaryActive: {
      fontWeight: 'bold',
      ...style?.summaryActive,
    },
    itemSummary: {
      minHeight: '64px',
      // lineHeight: "64px",
      alignItems: 'center',
      userSelect: 'none',
      cursor: 'pointer',
      display: 'flex',
      gap: '12px',
      justifyContent: variant === 'plus' ? 'flex-start' : 'space-between',

      color: COLORS.CHARCOAL,
      padding: '18px',
      width: '100%',
      textAlign: 'left',
      border: 'none',
      outline: 'none',
      ...style?.itemSummary,
    },
    itemTitle: {},
    itemSubtitle: {},
    itemDescription: {
      color: COLORS.CHARCOAL,
      overflow: 'hidden',
      lineHeight: '26px',
      ...style?.itemDescription,
    },
  };

  const toggleExpandedItem = (itemId) => {
    if (expandedItems.includes(itemId)) {
      if (multiple) {
        setExpandedItems(expandedItems.filter((item) => item !== itemId));
      } else {
        setExpandedItems([]);
      }
    } else if (multiple) {
      setExpandedItems([...expandedItems, itemId]);
    } else {
      setExpandedItems([itemId]);
    }
  };

  // Set default expanded items
  useEffect(() => {
    const newExpandedItems = items.filter((item) => item?.defaultOpen).map((item) => item.id);
    setExpandedItems(newExpandedItems);
  }, []);

  const ref = useRef(null);

  // TODO make this a hook
  const handleKeyPress = (event) => {
    // Blur on escape without selecting
    if (event.key === 'Escape') {
      setFocus(null);
    }

    // If press enter, select the hovered item
    if (event.key === 'Enter') {
      if (focus !== null) {
        toggleExpandedItem(focus);
      }
    }
  };

  return (
    <Box className={className} style={styles.container}>
      {items.map((item) => (
        <Box
          ref={ref}
          key={item?.id}
          style={{
            ...styles.item,
          }}
        >
          <Box
            tabIndex={0}
            onMouseDown={(e) => {
              e.preventDefault();
              toggleExpandedItem(item?.id);
              setFocus(null);
            }}
            // onClick={() => toggleExpandedItem(item?.id)}
            onMouseEnter={() => setHover(item?.id)}
            onMouseLeave={() => setHover(null)}
            onFocus={(e) => {
              console.log('e', e);
              setFocus(item?.id);
            }}
            onKeyDown={handleKeyPress}
            onBlur={() => setFocus(null)}
            style={{
              ...styles.itemSummary,
              ...(hover === item?.id && styles?.itemHover),
              ...(focus === item?.id && styles?.itemFocus),
              ...(expandedItems.includes(item?.id) && styles?.summaryActive),
            }}
          >
            {variant === 'plus' && (
              <Box
                style={{
                  border: `1px solid ${COLORS.SMOKE}`,
                  borderRadius: '30px',
                  width: '18px',
                  height: '18px',
                  minWidth: '18px',
                  minHeight: '18px',
                }}
              >
                {expandedItems.includes(item?.id) ? (
                  <IconMinus width='12' height='12' style={{ padding: '3px' }} />
                ) : (
                  <IconPlus width='12' height='12' style={{ padding: '3px' }} />
                )}
              </Box>
            )}

            <Spacer direction='vertical' gap='s'>
              {item?.summary}
              {!expandedItems.includes(item?.id) && item?.subtitle && (
                <Box style={styles.itemSubtitle}>{item?.subtitle}</Box>
              )}
            </Spacer>

            {(!variant || variant === 'default') && (
              <>
                {expandedItems.includes(item?.id) ? (
                  <AccordionChevron style={{ transform: 'rotate(180deg)' }} />
                ) : (
                  <AccordionChevron />
                )}
              </>
            )}
          </Box>
          <Box
            ref={itemRefs.current[item?.id]}
            style={{
              ...styles.itemDescription,
              display: expandedItems.includes(item?.id) ? 'block' : 'none',
            }}
          >
            {item?.details}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

Accordion.defaultProps = {
  className: '',
  style: {},
  InputProps: {},
  multiple: false,
  items: [],
  variant: 'default',
};

Accordion.propTypes = {
  className: PropTypes.string,
  style: PropTypes.shape({}),
  InputProps: PropTypes.shape({}),
  multiple: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.shape({ defaultOpen: PropTypes.bool, summary: PropTypes.string }),
  ),
  variant: PropTypes.string,
};
